import { logger } from '@/utils/logger/logger';
import { CONTENT_SECURITY_ERROR } from '@/utils/timelineEventSource';

/**
 * Shared onopen handler for SSE streams using @fortaine/fetch-event-source.
 *
 * Preserves the existing control flow:
 * - Non-OK responses: logs, calls onError with a prompt-injection-aware
 *   message, and **returns** (does NOT throw — avoids changing the library's
 *   retry/onerror path).
 * - OK responses: delegates to the caller's onOpen callback.
 *
 * Usage:
 *   onopen: (response) => handleStreamOpen({ response, logPrefix, onOpen, onError })
 */
export async function handleStreamOpen({
  response,
  logPrefix,
  onOpen,
  onError,
  context,
}: {
  response: Response;
  logPrefix: string;
  onOpen?: (response: Response) => Promise<void> | void;
  onError?: (error: unknown) => void;
  context?: Record<string, unknown>;
}): Promise<void> {
  if (!response.ok) {
    const errorMessage = await parseStreamErrorMessage(response);
    logger.error(`${logPrefix} onOpen error`, {
      status: response.status,
      ...context,
    });
    onError?.(new Error(errorMessage));
    return;
  }

  try {
    await onOpen?.(response);
  } catch (error) {
    logger.error(`${logPrefix} onOpen handler error`, error);
  }
}

/**
 * Parse a non-OK SSE response body and return a user-facing error message.
 *
 * - For prompt-injection errors: returns the backend message (or a default).
 * - For everything else: returns `"HTTP <status>"`.
 */
async function parseStreamErrorMessage(response: Response): Promise<string> {
  const errorText = await response.text().catch(() => '');

  let errorMessage = `HTTP ${response.status}`;
  try {
    if (errorText) {
      const parsed = JSON.parse(errorText) as {
        detail?: { type?: string; msg?: string };
      };
      if (
        typeof parsed.detail === 'object' &&
        parsed.detail !== null &&
        parsed.detail.type === CONTENT_SECURITY_ERROR.PROMPT_INJECTION
      ) {
        errorMessage =
          typeof parsed.detail.msg === 'string' && parsed.detail.msg
            ? parsed.detail.msg
            : CONTENT_SECURITY_ERROR.DEFAULT_MESSAGE;
      }
    }
  } catch {
    // Not JSON — keep default message
  }

  return errorMessage;
}
